import { cloneDeep, forEach, isPlainObject } from "lodash-es";

import config from "@/config";
import {
  getLocalUser,
  getManagerAccessFallbackPath,
  syncLocalUserProfile,
} from "@/constants/managerAccess";
import {
  TENANT_HEADER,
  getTabTenant,
  lockTabForTenantMismatch,
} from "@/utils/tenantGuard";
const API_URL_BASE = config.API_URL_BASE;

const METHOD_GET = "GET";
const METHOD_POST = "POST";
const METHOD_PUT = "PUT";
const METHOD_DELETE = "DELETE";

const OPTIONS = {
  method: null,
  headers: {
    "content-type": "application/json",
    "x-access-token": null,
    [TENANT_HEADER]: null,
  },
  body: null,
};

const AUTH_PAGES = [
  "/authentication/signin",
  "/authentication/signup",
  "/authentication/logout",
];

const prepareGetParameters = (params) => {
  const queryParamsArray = [];
  if (isPlainObject(params)) {
    forEach(params, (value, key) => {
      if (value === null || value === undefined) {
        return;
      }

      const encodedKey = encodeURIComponent(key.toString());
      const encodedValue = encodeURIComponent(value);
      queryParamsArray.push(`${encodedKey}=${encodedValue}`);
    });
  }
  const queryParamsString =
    queryParamsArray.length > 0 ? queryParamsArray.join("&") : "";

  return queryParamsString;
};

const getOptions = (method, payload) => {
  const copy = cloneDeep(OPTIONS);
  copy.method = method;
  copy.headers["x-access-token"] = localStorage.getItem("accessToken");
  copy.headers[TENANT_HEADER] = getTabTenant();
  copy.body = JSON.stringify(payload);
  return copy;
};

const parseResponseData = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }

  const text = await response.text();
  return text ? { message: text } : {};
};

const refreshLocalUserProfile = async () => {
  const API_URL = `${API_URL_BASE}/myprofile`;
  const response = await fetch(API_URL, getOptions(METHOD_GET));

  if (!response.ok) {
    return null;
  }

  const responseData = await parseResponseData(response);
  return syncLocalUserProfile(responseData);
};

const ANALYTICS_PATH = "ecommerce/analytics";
const analytics = {
  list: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getStaffLoad: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/staffLoad`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getOverallStaffLoad: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/staffLoadOverall`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getServiceOrders: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/serviceOrders`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getAdditionalServices: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/additionalServices`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getSellableServices: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/sellableServices`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getADR: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/adr`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getByAdministrators: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ANALYTICS_PATH}/byAdministrators`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const BRANCHES_PATH = "settings/branches";
const branches = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  isAcquiringEnabled: async () => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/isAcquiringEnabled`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  setPaymentProviderName: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/setPaymentProviderName`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  getPaymentProvidersInfo: async (id) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/getPaymentProvidersInfo`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  updateFinlineInfo: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateFinlineInfo`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updateLiqpayInfo: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateLiqpayInfo`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updateMonobankSubmerchantInfo: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateMonobankSubmerchantInfo`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updateMonobankInstalmentsInfo: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateMonobankInstalmentsInfo`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updateMonobankInstalmentsEnabled: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateMonobankInstalmentsEnabled`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updateMonobankInstalmentsPartsCount: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${BRANCHES_PATH}/${id}/updateMonobankInstalmentsPartsCount`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const CLIENTS_PATH = "ecommerce/clients";
const clients = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id, params = null) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}/${id}${queryParamsString ? `?${queryParamsString}` : ""}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  deleteDocument: async (id, key) => {
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}/${id}/document`;
    const options = getOptions(METHOD_DELETE, { key });
    return await executeFetch(API_URL, options);
  },
  deleteAdditionalDocument: async (id, key) => {
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}/${id}/additionalDocument`;
    const options = getOptions(METHOD_DELETE, { key });
    return await executeFetch(API_URL, options);
  },
  downloadFile: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${CLIENTS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    options.responseType = "blob";

    return fetch(API_URL, options);
  },
};

const executeFetch = async (API_URL, options) => {
  const response = await fetch(API_URL, options);
  const responseData = await parseResponseData(response);

  if (
    response.status === 409 &&
    responseData?.errorCode === "tenant-mismatch"
  ) {
    // Another tab logged into a different tenant and overwrote the shared
    // access token. Lock this (now stale) tab instead of acting on the wrong
    // tenant. See src/utils/tenantGuard.js.
    lockTabForTenantMismatch();
    return {};
  }

  if (response.status === 401) {
    return {};
  }

  if (
    response.status === 403 &&
    !AUTH_PAGES.includes(window.location.pathname)
  ) {
    if (responseData?.error === "access-denied") {
      const user = (await refreshLocalUserProfile()) || getLocalUser();
      const fallbackPath = getManagerAccessFallbackPath(user);
      if (window.location.pathname !== fallbackPath) {
        window.location.href = fallbackPath;
      }
      return {};
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("localUser");
    localStorage.removeItem("showDevFeatures");
    // localStorage.removeItem("hideDevFeaturesAnyway");

    // window.location.href = "/authentication/logout?reason=jwtExpired";
    window.location.href = "/authentication/logout";
    return;
  }

  if (response.status === 403) {
    return {};
  }

  if (response.status >= 400) {
    const error = new Error(responseData?.message);

    if (responseData.errorMessage) {
      error.errorMessage = responseData.errorMessage;
    }

    error.status = response.status;

    if (responseData?.errors) {
      error.data = responseData.errors;
    }

    throw error;
  }

  return responseData;
};

const COMPANY_PATH = "settings/company";
const company = {
  show: async () => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (payload) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  getLinkedTenants: async () => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/getLinkedTenants`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getApiCredentials: async () => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/apiCredentials`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  sendInstantMessage: async (
    sender,
    recipients,
    text,
    purpose,
    serviceOrderId,
    startTime,
  ) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/sendInstantMessage`;
    const options = getOptions(METHOD_POST, {
      sender,
      recipients,
      text,
      purpose,
      serviceOrderId,
      startTime,
    });
    return await fetch(API_URL, options); // (!) not used `executeFetch` wrapper
  },

  renderInvoiceMessageTemplate: async (
    orderId,
    paymentId,
    amountOverride = null,
    channel = "sms",
    isSpa = false,
    saleId = null,
  ) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/renderInvoiceMessageTemplate`;
    const options = getOptions(METHOD_POST, {
      orderId,
      paymentId,
      amountOverride,
      channel,
      isSpa,
      saleId,
    });
    return await executeFetch(API_URL, options);
  },

  getInstantMessageStatuses: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/instantMessageStatuses?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getSmsHistory: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/smsHistory?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getInstantMessageTopUps: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/instantMessageTopUps?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  instantMessageTopUp: async (payload) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/instantMessageTopUp`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getInstantMessageBalance: async () => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/instantMessageBalance`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getInstantMessagePackages: async () => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/instantMessagePackages`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  channelManager: async (payload) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/channelManager`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getEmailDomains: async (apiKey) => {
    const API_URL = `${API_URL_BASE}/${COMPANY_PATH}/emailDomains`;
    const options = getOptions(METHOD_POST, { apiKey });
    return await executeFetch(API_URL, options);
  },
};

const MANAGERS_PATH = "settings/managers";
const managers = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);

    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  restoreOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}/${id}/restore`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  startEmailConfirmation: async (id, webAppBaseURL, email) => {
    const API_URL = `${API_URL_BASE}/${MANAGERS_PATH}/${id}/startEmailConfirmation`;
    const options = getOptions(METHOD_POST, { webAppBaseURL, email });
    return await executeFetch(API_URL, options);
  },
};

const MYPROFILE_PATH = "myprofile";
const myprofile = {
  show: async () => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getAvailableTenants: async () => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}/getAvailableTenants`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getTenantSlug: async () => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}/getTenantSlug`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  setTenantSlug: async (payload) => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}/setTenantSlug`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  switchTenant: async (payload) => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}/switchTenant`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (payload) => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  startEmailConfirmation: async (webAppBaseURL, email) => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}/startEmailConfirmation`;
    const options = getOptions(METHOD_POST, { webAppBaseURL, email });
    return await executeFetch(API_URL, options);
  },
  logout: async () => {
    const API_URL = `${API_URL_BASE}/authentication/logout`;
    const options = getOptions(METHOD_POST, {});
    return await executeFetch(API_URL, options);
  },
  remove: async () => {
    const API_URL = `${API_URL_BASE}/${MYPROFILE_PATH}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const MAINTENANCE_PATH = "maintenance";
const maintenance = {
  getMigrations: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/getMigrations`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  runMigrations: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/runMigrations`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  runCronScript: async (name) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/${name}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getTenantsList: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/getTenantsList`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  tenantsLinking: async (payload) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/tenantsLinking`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  getTenants: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/getTenants`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  removeTenant: async (id) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/removeTenant/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },

  setTenantPauseOn: async (id) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/setTenantPauseOn/${id}`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  setTenantPauseOff: async (id) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/setTenantPauseOff/${id}`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },

  getMonobankSubmerchants: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/getMonobankSubmerchants`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  setTenantSubscriptionStatus: async (id, subscriptionStatus) => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/setTenantSubscriptionStatus/${id}`;
    const options = getOptions(METHOD_POST, { subscriptionStatus });
    return await executeFetch(API_URL, options);
  },

  checkServiceOrdersForPrepayment: async () => {
    const API_URL = `${API_URL_BASE}/${MAINTENANCE_PATH}/checkServiceOrdersForPrepayment`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
};

const POSITIONS_PATH = "settings/positions";
const positions = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  removePhoto: async (payload) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/removePhoto`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
  removePhotoAdditional: async (id, photoId) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/${id}/removePhotoAdditional/${photoId}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  sortPhotos: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${POSITIONS_PATH}/${id}/photos/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const HOTEL_DISCOUNTS_PATH = "settings/hotelDiscounts";
const hotelDiscounts = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${HOTEL_DISCOUNTS_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const SERVICES_PATH = "settings/services";
const services = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const ADDITIONAL_SERVICES_PATH = "settings/additional-service";
const additionalServices = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  delete: async (id) => {
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ADDITIONAL_SERVICES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const FEEDBACK_ATTRIBUTE_PATH = "settings/feedback-attribute";
const feedbackAttributes = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  delete: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const FEEDBACK_PATH = "feedback";
const feedbacks = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  byOrder: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_PATH}/byOrder?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const FEEDBACK_ATTRIBUTE_SPA_PATH = "settings/feedback-attribute-spa";
const feedbackAttributesSpa = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_SPA_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_SPA_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_SPA_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_SPA_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  delete: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_ATTRIBUTE_SPA_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const FEEDBACK_SPA_PATH = "feedback-spa";
const feedbacksSpa = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_SPA_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_SPA_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  byOrder: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FEEDBACK_SPA_PATH}/byOrder?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FEEDBACK_SPA_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const FINANCE_CATEGORY_PATH = "finance/category";
const financeCategories = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${FINANCE_CATEGORY_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${FINANCE_CATEGORY_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${FINANCE_CATEGORY_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FINANCE_CATEGORY_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  delete: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${FINANCE_CATEGORY_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
};

const SERVICE_ORDERS_PATH = "ecommerce/service-orders";
const serviceOrders = {
  getConfig: async () => {
    const currentTimestamp = Date.now();
    const queryString = `currentTimestamp=${currentTimestamp}`;
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/config?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  fetchReservations: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/fetchReservations`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  getDashboard: async (date) => {
    const queryString = date ? `date=${date}` : "";
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/dashboard?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getInventory: async (start, end) => {
    const queryString = `start=${start}&end=${end}`;
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/getInventory?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  syncStaffRates: async (start, end) => {
    const queryString = `start=${start}&end=${end}`;
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/syncStaffRates?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  setZeroStaffRates: async (start, end) => {
    const queryString = `start=${start}&end=${end}`;
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/setZeroStaffRates?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getAvailableDates: async (staffId, serviceOrderId) => {
    let queryString = `staffId=${staffId}`;
    if (serviceOrderId) {
      queryString = `${queryString}&serviceOrderId=${serviceOrderId}`;
    }
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/available-dates?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getFreeSlots: async (date, staffId, duration, currentOrderId) => {
    const queryString = `date=${date}&staffId=${staffId}&duration=${duration}&currentOrderId=${currentOrderId}`;
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/free-slots?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  list: async (params, { signal } = {}) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    if (signal) options.signal = signal;
    return await executeFetch(API_URL, options);
  },
  downloadFile: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    options.responseType = "blob";

    // Set 90 second timeout for file download
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    options.signal = controller.signal;

    try {
      const response = await fetch(API_URL, options);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  },
  findOne: async (id, { signal } = {}) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    if (signal) options.signal = signal;
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  createMany: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/createMany`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },

  addDiscount: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/addDiscount`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addDiscountDraft: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/addDiscountDraft`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removeDiscount: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/removeDiscount`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  addPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/addPayment`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addPaymentDraft: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/addPaymentDraft`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removePayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/removePayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  refundPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/refundPayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updatePaymentStatus: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/updatePaymentStatus`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  getPriceBreakdown: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/getPriceBreakdown`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  addHistoryUpdate: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/addHistoryUpdate`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  applyPromocode: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/promocode`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removePromocode: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/promocode`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },

  getInstantMessages: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/instantMessages`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  updateInstantMessage: async (id, messageKey, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/instantMessages/${messageKey}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  cancelInstantMessage: async (id, messageKey) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/instantMessages/${messageKey}/cancel`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },

  getEmails: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/emails`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getCheckin: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/checkin`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  deleteCheckinDocument: async (id, key) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/checkin/document`;
    const options = getOptions(METHOD_DELETE, { key });
    return await executeFetch(API_URL, options);
  },

  deleteCheckinSignature: async (id) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/checkin/signature`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },

  sendEmail: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/sendEmail`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  updateEmail: async (id, emailId, payload) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/emails/${emailId}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  cancelEmail: async (id, emailId) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/emails/${emailId}/cancel`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },

  renderEmailText: async (id, templateType) => {
    const API_URL = `${API_URL_BASE}/${SERVICE_ORDERS_PATH}/${id}/emails/renderEmailText`;
    const options = getOptions(METHOD_POST, {
      templateType,
    });
    return await executeFetch(API_URL, options);
  },

  renderSMSText: async (payload) => {
    const API_URL = `${API_URL_BASE}/settings/company/renderSmsMessageTemplate`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const PROMOCODES_PATH = "promocodes";
const promocodes = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  listHistory: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${PROMOCODES_PATH}/history?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  updateSettings: async (payload) => {
    const enabled = payload?.enabled === true;
    await company.update({
      promocodes: {
        enabled,
      },
    });
    return { enabled };
  },
};

const BOOKING_INVOICE_PATH = "ecommerce/service-orders-booking-invoice";
const bookingInvoices = {
  listBookingInvoices: async (sequenceId, groupSequenceId) => {
    const queryString = prepareGetParameters({ sequenceId, groupSequenceId });
    const API_URL = `${API_URL_BASE}/${BOOKING_INVOICE_PATH}/booking-invoices?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  createBookingInvoice: async (payload) => {
    const API_URL = `${API_URL_BASE}/${BOOKING_INVOICE_PATH}/booking-invoices`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateBookingInvoice: async (invoiceId, payload) => {
    const API_URL = `${API_URL_BASE}/${BOOKING_INVOICE_PATH}/booking-invoices/${invoiceId}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  deleteBookingInvoice: async (invoiceId) => {
    const API_URL = `${API_URL_BASE}/${BOOKING_INVOICE_PATH}/booking-invoices/${invoiceId}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const PAYMENT_ACCOUNTS_PATH = "ecommerce/payment-accounts";
const paymentAccounts = {
  list: async (includeInactive) => {
    let API_URL = `${API_URL_BASE}/${PAYMENT_ACCOUNTS_PATH}`;
    if (includeInactive) {
      API_URL += "?includeInactive=true";
    }
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PAYMENT_ACCOUNTS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${PAYMENT_ACCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  remove: async (id) => {
    const API_URL = `${API_URL_BASE}/${PAYMENT_ACCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const ACQUIRING_ACCOUNTS_PATH = "settings/acquiring-accounts";
const acquiringAccounts = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  show: async (id) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  update: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  remove: async (id) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },

  setActive: async (id) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}/${id}/set-active`;
    const options = getOptions(METHOD_PUT);
    return await executeFetch(API_URL, options);
  },

  updateRoomMapping: async (payload) => {
    const API_URL = `${API_URL_BASE}/${ACQUIRING_ACCOUNTS_PATH}/room-mapping/update`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const PAYMENTS_PATH = "ecommerce/payments";
const payments = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getServiceOrderInvoice: async (paymentId) => {
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/getServiceOrderInvoice/${paymentId}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  getInvoiceDetails: async (paymentId) => {
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/getInvoiceDetails/${paymentId}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },

  addTransactions: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/transactions`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },

  updateTransaction: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/transaction/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  updateTransactionComment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/transaction/${id}/comment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },

  downloadFile: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    options.responseType = "blob";

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    options.signal = controller.signal;

    try {
      const response = await fetch(API_URL, options);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  },

  deleteTransaction: async (id, params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${PAYMENTS_PATH}/transaction/${id}?${queryParamsString}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const STAFF_PATH = "settings/staff";
const staff = {
  list: async (params, { signal } = {}) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    if (signal) options.signal = signal;
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  getWorkingHoursRange: async (payload, { signal } = {}) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/workingHoursRange`;
    const options = getOptions(METHOD_POST, payload);
    if (signal) options.signal = signal;
    return await executeFetch(API_URL, options);
  },
  constructorSaveWorkingHours: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/constructorSaveWorkingHours`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  createCopy: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}/createCopy`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  resetPassword: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}/resetPassword`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  removePhoto: async (payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/removePhoto`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
  removePhotoAdditional: async (id, photoId) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}/removePhotoAdditional/${photoId}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sortPhotos: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${id}/photos/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  getAdditionalServices: async (staffId) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/getAdditionalServices`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getAdditionalServicesForGuests: async (staffId, guests, dates = {}) => {
    let API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/getAdditionalServices/guests/${guests}`;
    const query = [];
    if (dates.serviceDateFrom) {
      query.push(`serviceDateFrom=${dates.serviceDateFrom}`);
    }
    if (dates.serviceDateTo) {
      query.push(`serviceDateTo=${dates.serviceDateTo}`);
    }
    if (query.length) {
      API_URL += `?${query.join("&")}`;
    }
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  addAdditionalService: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/createAdditionalService`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateAdditionalService: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/updateAdditionalService/${payload._id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeAdditionalService: async (staffId, id) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/removeAdditionalService/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  getAmenities: async (staffId = "") => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/getAmenities?staffId=${staffId}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  setAmenities: async (staffId, amenities) => {
    const payload = {
      staffId,
      amenities,
    };
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/setAmenities`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  getSyncUrls: async (staffId) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/getSyncUrls`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  getSyncUrlExportByChannelName: async (staffId, channelName) => {
    const queryParamsString = prepareGetParameters({
      channelName,
    });
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/getSyncUrlExportByChannelName?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  addSyncUrl: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/createSyncUrl`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateSyncUrl: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/updateSyncUrl/${payload._id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeSyncUrl: async (staffId, id) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/removeSyncUrl/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  refreshSyncUrl: async (staffId, id) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/refreshSyncUrl/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  updateStaffGuestSettings: async (staffId, payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_PATH}/${staffId}/updateStaffGuestSettings`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const SPA_RESOURCES_PATH = "spa/resources";
const spaResources = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  removePhoto: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/removePhoto`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
  getWorkingHoursRange: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/workingHoursRange`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  constructorSaveWorkingHours: async (resourceId, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCES_PATH}/${resourceId}/constructorSaveWorkingHours`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const SPA_RESOURCE_CATEGORIES_PATH = "spa/resource-categories";
const spaResourceCategories = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  removePhoto: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}/removePhoto`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_RESOURCE_CATEGORIES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const SELLABLE_SERVICES_PATH = "ecommerce/sellable-services";
const sellableServices = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  dashboardSales: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICES_PATH}/dashboard-sales?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
};

const SELLABLE_SERVICE_CATEGORIES_PATH =
  "ecommerce/sellable-service-categories";
const sellableServiceCategories = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SELLABLE_SERVICE_CATEGORIES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const SALES_PATH = "ecommerce/sales";
const sales = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SALES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  availability: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/availability?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  addPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}/addPayment`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addPaymentDraft: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/addPaymentDraft`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removePayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}/removePayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  refundPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}/refundPayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updatePaymentStatus: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}/updatePaymentStatus`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  sendEmail: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SALES_PATH}/${id}/sendEmail`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const SPA_SERVICES_PATH = "spa/services";
const spaServices = {
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  sort: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/sort`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  createCopy: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/${id}/createCopy`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  removePhoto: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/removePhoto`;
    const options = getOptions(METHOD_DELETE, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_SERVICES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const SPA_ORDERS_PATH = "spa/orders";
const spaOrders = {
  getConfig: async () => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/config`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  downloadFile: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    options.responseType = "blob";

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    options.signal = controller.signal;

    try {
      const response = await fetch(API_URL, options);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  getPriceBreakdown: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/getPriceBreakdown`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addDiscountDraft: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/addDiscountDraft`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addDiscount: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/addDiscount`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removeDiscount: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/removeDiscount`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  addPaymentDraft: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/addPaymentDraft`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  addPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/addPayment`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removePayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/removePayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  updatePaymentStatus: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/updatePaymentStatus`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  refundPayment: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/refundPayment`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  addHistoryUpdate: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/addHistoryUpdate`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  applyPromocode: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/promocode`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removePromocode: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/promocode`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  getEmails: async (id) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/emails`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  sendEmail: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/sendEmail`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateEmail: async (id, emailId, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/emails/${emailId}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  cancelEmail: async (id, emailId) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/emails/${emailId}/cancel`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  renderEmailText: async (id, templateType) => {
    const API_URL = `${API_URL_BASE}/${SPA_ORDERS_PATH}/${id}/emails/renderEmailText`;
    const options = getOptions(METHOD_POST, { templateType });
    return await executeFetch(API_URL, options);
  },
};

const MASTER_SERVICE_ORDERS_PATH = "ecommerce/master-service-orders";
const masterServiceOrders = {
  ensure: async ({ kind, orderId }) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/ensure`;
    const options = getOptions(METHOD_POST, { kind, orderId });
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  transactions: async (id) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/${id}/transactions`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  listInvoices: async (id) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/${id}/invoices`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  createInvoice: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/${id}/invoices`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  removeInvoice: async (id, invoiceId) => {
    const API_URL = `${API_URL_BASE}/${MASTER_SERVICE_ORDERS_PATH}/${id}/invoices/${invoiceId}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const SPA_BOOKING_INVOICE_PATH = "spa/orders-booking-invoice";
const spaBookingInvoices = {
  listBookingInvoices: async (sequenceId) => {
    const queryString = prepareGetParameters({ sequenceId });
    const API_URL = `${API_URL_BASE}/${SPA_BOOKING_INVOICE_PATH}/booking-invoices?${queryString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  createBookingInvoice: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_BOOKING_INVOICE_PATH}/booking-invoices`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateBookingInvoice: async (invoiceId, payload) => {
    const API_URL = `${API_URL_BASE}/${SPA_BOOKING_INVOICE_PATH}/booking-invoices/${invoiceId}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  deleteBookingInvoice: async (invoiceId) => {
    const API_URL = `${API_URL_BASE}/${SPA_BOOKING_INVOICE_PATH}/booking-invoices/${invoiceId}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
};

const STAFF_RATE_PATH = "settings/staffRate";
const staffRates = {
  getRoomCodes: async () => {
    const API_URL = `${API_URL_BASE}/${STAFF_RATE_PATH}/roomCodes`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  list: async (params) => {
    const queryParamsString = prepareGetParameters(params);
    const API_URL = `${API_URL_BASE}/${STAFF_RATE_PATH}?${queryParamsString}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  update: async (payload) => {
    const API_URL = `${API_URL_BASE}/${STAFF_RATE_PATH}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  syncPricesFromChannelManagerToInternal: async (payload = {}) => {
    const API_URL = `${API_URL_BASE}/${STAFF_RATE_PATH}/syncPricesFromChannelManagerToInternal`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const USER_PATH = "settings/user";
const user = {
  resetPassword: async (userId, payload) => {
    const API_URL = `${API_URL_BASE}/${USER_PATH}/${userId}/resetPassword`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const PARTNER_INTEGRATION_PATH = "settings/partnerIntegrations";
const partnerIntegration = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  show: async (name) => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/${name}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  update: async (name, payload) => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/${name}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  finishPOSTerSetup: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/finishPOSTerSetup`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  manysalesStatus: async () => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/manysales/status`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  manysalesConnect: async () => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/manysales/connect`;
    const options = getOptions(METHOD_POST, {});
    return await executeFetch(API_URL, options);
  },
  manysalesCallback: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PARTNER_INTEGRATION_PATH}/manysales/callback`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const CURRENCIES_PATH = "settings/currencies";
const currencies = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${CURRENCIES_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
};

const COUNTRIES_PATH = "settings/countries";
const countries = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${COUNTRIES_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
};

const PRODUCTS_PATH = "settings/products";
const products = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  activate: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/activate`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  checkout: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/checkout`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  deactivate: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/deactivate`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  cancelSubscription: async () => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/cancel-subscription`;
    const options = getOptions(METHOD_POST);
    return await executeFetch(API_URL, options);
  },
  changeNextPaymentDate: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/next-payment-date`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  listPayments: async () => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/payments`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  pricePreview: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/price-preview`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  getUpgradeDeltaPrice: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/upgrade-delta-price`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  chargeUpgrade: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/charge-upgrade`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  downgrade: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/downgrade`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  downgradeStaffMax: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/downgrade-staff-max`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  downgradeSpaResourceMax: async (payload) => {
    const API_URL = `${API_URL_BASE}/${PRODUCTS_PATH}/downgrade-spa-resource-max`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const IMAGE_GENERATOR_PATH = "settings/image-generator";
const imageGenerator = {
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${IMAGE_GENERATOR_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
};

const SUBSCRIPTION_INVOICES_PATH = "subscription/invoices";
const subscriptionInvoices = {
  list: async () => {
    const API_URL = `${API_URL_BASE}/${SUBSCRIPTION_INVOICES_PATH}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  findOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SUBSCRIPTION_INVOICES_PATH}/${id}`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  create: async (payload) => {
    const API_URL = `${API_URL_BASE}/${SUBSCRIPTION_INVOICES_PATH}`;
    const options = getOptions(METHOD_POST, payload);
    return await executeFetch(API_URL, options);
  },
  updateOne: async (id, payload) => {
    const API_URL = `${API_URL_BASE}/${SUBSCRIPTION_INVOICES_PATH}/${id}`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
  removeOne: async (id) => {
    const API_URL = `${API_URL_BASE}/${SUBSCRIPTION_INVOICES_PATH}/${id}`;
    const options = getOptions(METHOD_DELETE);
    return await executeFetch(API_URL, options);
  },
  getRequisites: async () => {
    const API_URL = `${API_URL_BASE}/subscription/requisites`;
    const options = getOptions(METHOD_GET);
    return await executeFetch(API_URL, options);
  },
  updateRequisites: async (payload) => {
    const API_URL = `${API_URL_BASE}/subscription/requisites`;
    const options = getOptions(METHOD_PUT, payload);
    return await executeFetch(API_URL, options);
  },
};

const api = {
  company,
  analytics,
  bookingInvoices,
  branches,
  clients,
  hotelDiscounts,
  maintenance,
  managers,
  myprofile,
  positions,
  services,
  additionalServices,
  feedbackAttributes,
  feedbackAttributesSpa,
  feedbacks,
  feedbacksSpa,
  financeCategories,
  serviceOrders,
  masterServiceOrders,
  promocodes,
  paymentAccounts,
  acquiringAccounts,
  payments,
  spaOrders,
  spaBookingInvoices,
  spaResources,
  spaResourceCategories,
  spaServices,
  sellableServices,
  sellableServiceCategories,
  sales,
  staff,
  staffRates,
  user,
  partnerIntegration,
  currencies,
  countries,
  imageGenerator,
  products,
  subscriptionInvoices,
};

export default api;
